import { createContext, useState, useDemo } from "react";
import { createTheme } from "@mui/material/styles";
import { useMemo } from "react";

export const tokens = (mode) => ({
  ...(mode === "dark"
    ? {
        grey: {
          100: "#e9eaea",
          200: "#d4d5d6",
          300: "#bebfc1",
          400: "#a9aaad",
          500: "#939598",
          600: "#76777a",
          700: "#58595b",
          800: "#3b3c3d",
          900: "#1d1e1e",
        },
        primary: {
          100: "#d6d6d7",
          200: "#acacaf",
          300: "#838386",
          400: "#59595e",
          500: "#303036",
          600: "#26262b",
          700: "#1d1d20",
          800: "#131316",
          900: "#0a0a0b",
        },
        greenAccent: {
          100: "#dbf5ee",
          200: "#b7ebde",
          300: "#94e2cd",
          400: "#70d8bd",
          500: "#4cceac",
          600: "#3da58a",
          700: "#2e7c67",
          800: "#1e5245",
          900: "#0f2922",
        },
        redAccent: {
          100: "#f8dcdb",
          200: "#f1b9b7",
          300: "#e99592",
          400: "#e2726e",
          500: "#db4f4a",
          600: "#af3f3b",
          700: "#832f2c",
          800: "#58201e",
          900: "#2c100f",
        },
        blueAccent: {
          100: "#d5f0f8",
          200: "#abe1f0",
          300: "#81d3e9",
          400: "#57c4e1",
          500: "#2db5da",
          600: "#2491ae",
          700: "#1b6d83",
          800: "#124857",
          900: "#09242c",
        },
      }
    : {
        grey: {
          100: "#1d1e1e",
          200: "#3b3c3d",
          300: "#58595b",
          400: "#76777a",
          500: "#939598",
          600: "#a9aaad",
          700: "#bebfc1",
          800: "#F5F6F7",
          900: "#e9eaea",
        },
        primary: {
          100: "#0a0a0b",
          200: "#131316",
          300: "#1d1d20",
          400: "#26262b",
          500: "#303036",
          600: "#59595e",
          700: "#d6d6d7",
          800: "#acacaf",
          900: "#d6d6d7",
        },
        greenAccent: {
          100: "#0f2922",
          200: "#1e5245",
          300: "#2e7c67",
          400: "#3da58a",
          500: "#4cceac",
          600: "#70d8bd",
          700: "#94e2cd",
          800: "#b7ebde",
          900: "#dbf5ee",
        },
        redAccent: {
          100: "#2c100f",
          200: "#58201e",
          300: "#832f2c",
          400: "#af3f3b",
          500: "#db4f4a",
          600: "#e2726e",
          700: "#e99592",
          800: "#f1b9b7",
          900: "#f8dcdb",
        },
        blueAccent: {
          100: "#09242c",
          200: "#124857",
          300: "#1b6d83",
          400: "#2491ae",
          500: "#2db5da",
          600: "#57c4e1",
          700: "#81d3e9",
          800: "#abe1f0",
          900: "#d5f0f8",
        },
      }),
});

export const themeSettings = (mode, direction="ltr") => {
  const colors = tokens(mode);

  return {
    direction: direction,
    palette: {
      mode: mode,
      ...(mode === "dark"
        ? {
            primary: { main: "#e0f2f1" }, // Same core navy
            secondary: { main: "#00b4d8" }, // Consistent accent
            error: { main: "#ef5350" },
            warning: { main: "#ffa726" },
            info: { main: "#29b6f6" },
            success: { main: "#66bb6a" },
            background: {
              default: "#0d1b2a", // Deep navy
              paper: "#0d1b2a", // Slightly raised cards
            },
            text: {
              primary: "#e0f2f1", // Soft white
              secondary: "#b0bec5", // Dim gray-blue
            },
          }
        : {
            primary: { main: "#12233a" }, // Elegant navy
            secondary: { main: "#00b4d8" }, // Aqua blue accent
            error: { main: "#f44336" }, // Soft red
            warning: { main: "#ff9800" }, // Warm amber
            info: { main: "#2196f3" }, // Sky blue
            success: { main: "#4caf50" }, // Classic green
            background: {
              default: "#f5f7fa", // Light gray page
              paper: "#ffffff", // Cards
            },
            text: {
              primary: "#1e293b", // Dark gray
              secondary: "#64748b", // Muted
            },
          }),
    },
    typography: {
      fontFamily: ["Source Sans Pro", "sans-serif"].join(","),
      fontSize: 12,
      h1: {
        fontFamily: ["Source Sans Pro", "sans-serif"].join(","),
        fontSize: 40,
      },
      h2: {
        fontFamily: ["Source Sans Pro", "sans-serif"].join(","),
        fontSize: 32,
      },
      h3: {
        fontFamily: ["Source Sans Pro", "sans-serif"].join(","),
        fontSize: 24,
      },
      h4: {
        fontFamily: ["Source Sans Pro", "sans-serif"].join(","),
        fontSize: 20,
      },
      h5: {
        fontFamily: ["Source Sans Pro", "sans-serif"].join(","),
        fontSize: 16,
      },
      h6: {
        fontFamily: ["Source Sans Pro", "sans-serif"].join(","),
        fontSize: 14,
      },
    },
    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            textTransform: "none",
            color:"#00b4d8"
          },
          containedPrimary: {
            backgroundColor: "#00b4d8",
            color: "#ffffff",
            "&:hover": {
              backgroundColor: "#0096c7",
            },
          },
          outlinedPrimary: {
            borderColor: "#00b4d8",
            color: "#00b4d8",
            "&:hover": {
              backgroundColor: "rgba(0,180,216,0.1)",
            },
          },
        },
        defaultProps: {
          disableElevation: true,
        },
      },
    },
  };
};

export const ColorModeContext = createContext({
  toggleColorMode: () => {},
});

export const useMode = (direction) => {
  const [mode, setMode] = useState("dark");

  const colorMode = useMemo(
    () => ({
      toggleColorMode: () =>
        setMode((prev) => (prev === "dark" ? "light" : "dark")),
    }),
    []
  );

  const theme = useMemo(() => createTheme(themeSettings(mode, direction)), [mode, direction]);
  return [theme, colorMode];
};

export const getTheme = (direction = "ltr") =>
  createTheme({
    direction,
    typography: {
      fontFamily:
        direction === "rtl" ? "Tajawal, sans-serif" : "Roboto, sans-serif",
    },
  });
